import { useHasProperty, usePersistedState, useSetState } from "@/hooks";
import { Text } from "@/includes";
import { client } from "@/index";
import { potionsLocalStorage } from "@/services/LocalStorage/localStorage";
import { sendNotification } from "@/services/redux/actions";
import { RootState } from "@/services/redux/store";
import { experiencesUtils } from "@/utils";
import { FLAGS_ENUM } from "@/utils/enums/flags";
import { INTEGRATION_CATEGORY_ENUM } from "@/utils/enums/integrations";
import { EdgeType } from "@/utils/types/requests";
import { ApolloQueryResult, useQuery } from "@apollo/client";
import { useFsFlag } from "@flagship.io/react-sdk";
import { useTheme } from "@mui/material";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import { getColumns } from "./columns";
import { DUPLICATE_EXPERIENCE } from "./components/DuplicateOnOtherSiteDialog/requests";
import Overview from "./components/Overview";
import {
  MAPPED_COLUMNS_TO_HIDE,
  mapSearchTypeToKey,
  mapSearchTypeToKeyBuilder,
} from "./enums";
import { compareExperiences } from "./helpers";
import { GET_EXPERIENCES, GET_INTEGRATIONS } from "./requests";
import { LabelType, searchType } from "./types";

const getOverviewData = (columnToShow: string, experience: Dic<any>) => {
  if (!experience.isMonitorable) return <Text> - </Text>;
  return <Overview columnToShow={columnToShow} experience={experience} />;
};

const useExperienceList = (
  searchType: searchType,
  searchExp: string,
  handleOpenDuplicateOnOtherSite: (
    experienceId: string,
    experienceName: string,
  ) => void,
  labels: Array<LabelType>,
  refetchLabels: (
    variables?:
      | Partial<{
          siteId: any;
          first: number;
        }>
      | undefined,
  ) => Promise<ApolloQueryResult<Dic<any>>>,
  selectedLabels: Array<number> = [],
) => {
  const siteId = useSelector((state: RootState) => state.site.siteId);
  const organisationId = useSelector(
    (state: RootState) => state.organisation.organisationId,
  );
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t }: i18translateType = useTranslation();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const devMode = useSelector(
    (state: RootState) => state.developerMode.isEnabled,
  );
  const flagEarlyAdopterBuilder = useFsFlag("early_adopter_builder");
  const flagValueEarlyAdopterBuilder = flagEarlyAdopterBuilder.getValue(false);

  const {
    hideCatalog,
    hasEmail,
    hasCmsTools,
    hideReport,
    hideMerch,
    hasBuilder,
    flagValueHasMerchDeployment,
  } = useHasProperty();
  const [currentPage, setCurrentPage] = React.useState(0);
  const [cursor, setCursor] = React.useState<string>("");
  const [totalCount, setTotalCount] = React.useState(0);
  const [pageSize, setPageSize] = usePersistedState({
    key: `pageSize-experiences`,
    initialValue: 25,
  });
  const [pageInfos, setPageInfos] = React.useState({
    endCursor: "",
    hasNextPage: false,
    hasPreviousPage: false,
    startCursor: "",
  });
  const handleChangePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    setCursor("");
  };
  const next = () => {
    setCursor(pageInfos.endCursor);
    setCurrentPage(currentPage + 1);
  };
  const previous = () => {
    setCursor(pageInfos.startCursor);
    setCurrentPage(currentPage - 1);
  };

  // ----------------- Deployment ----------------------------------
  const { data: dataIntegrations } = useQuery(GET_INTEGRATIONS, {
    variables: { siteId },
    skip: isEmpty(siteId),
  });

  const isCms = !isEmpty(
    dataIntegrations?.integrations?.edges?.find(
      (edge: EdgeType) => edge.node?.category === INTEGRATION_CATEGORY_ENUM.CMS,
    ),
  );

  const goToRecommendationDeploy = (experienceId: string) => {
    history.push(
      `/${organisationId}/${siteId}/recommendation/${experienceId}/deploy`,
    );
  };

  const [experienceToDelete, setExperienceToDelete] = useSetState({
    id: "",
    name: "",
  });
  const [status, setStatus] = React.useState<
    "" | "success" | "loading" | "error"
  >("loading");

  const canEdit = React.useMemo(() => {
    if (searchType === "MULTIPLY" && !hideCatalog) return true;
    if (searchType === "EMAILING" && hasEmail && !hideCatalog) return true;
    if (!hideCatalog && searchType === "EMERCH") return true;
    if (devMode && ["MULTIPLY", "EMAILING", "EMERCH"].includes(searchType))
      return true;
    return false;
  }, [searchType, hasCmsTools, hideCatalog, hasEmail, devMode]);

  const canCreate = React.useMemo(() => {
    if (searchType === "MULTIPLY" && !hideCatalog) return true;
    if (searchType === "EMAILING" && hasEmail && !hideCatalog) return true;
    if (!!hasCmsTools && searchType === "EMERCH") return true;
    if (devMode && ["MULTIPLY", "EMAILING", "EMERCH"].includes(searchType))
      return true;
    return false;
  }, [searchType, hasCmsTools, hideCatalog, hasEmail, devMode]);

  const columnsToFilter = MAPPED_COLUMNS_TO_HIDE[searchType];

  const flagHasMerchandisingReport = useFsFlag(
    FLAGS_ENUM.HAS_MERCHANDISING_REPORT,
  );
  const flagValueHasMerchandisingReport =
    flagHasMerchandisingReport.getValue(false);

  React.useEffect(() => {}, [
    devMode,
    searchType,
    hideCatalog,
    hasEmail,
    hasCmsTools,
    hasBuilder,
    hideReport,
    hideMerch,
    flagValueHasMerchDeployment,
    flagValueHasMerchandisingReport,
    canEdit,
  ]);

  const handleReportMerchandising = (id: string) => {
    history.push(`/${organisationId}/${siteId}/merchandising/${id}/reporting`);
  };

  const goToMerchandisingDeploy = (row: Dic<any>) => {
    history.push(`/${organisationId}/${siteId}/merchandising/${row.id}/deploy`);
  };

  const filteredColumns = React.useMemo(
    () =>
      getColumns(
        t,
        theme,
        devMode,
        handleOpenDuplicateOnOtherSite,
        hasBuilder,
        labels,
        refetchLabels,
        hideReport,
        (isCms || flagValueHasMerchDeployment) &&
          !hideMerch &&
          searchType === "EMERCH",
        goToMerchandisingDeploy,
        searchType === "MULTIPLY",
        goToRecommendationDeploy,
        searchType === "EMERCH" && flagValueHasMerchandisingReport,
        handleReportMerchandising,
      ).filter(
        (c: Dic<any>) => !columnsToFilter.find((cf: string) => cf === c.field),
      ),
    [anchorEl, hasBuilder, labels, devMode, hideReport, isCms, hideMerch],
  );

  const canDelete =
    !!["MULTIPLY", "EMAILING", "EMERCH"].includes(searchType) || devMode;

  const { data, loading, error, refetch } = useQuery(GET_EXPERIENCES, {
    variables: {
      siteId: parseInt(siteId),
      cursor: cursor,
      first: pageSize,
      searchType,
      search: searchExp,
      sort: "created_at desc, id desc",
      labels: selectedLabels,
    },
    onCompleted: (res: Dic<any>) => {
      setTotalCount(res.experiences.totalCount);
      setPageInfos(res.experiences.pageInfo);
    },
  });

  React.useEffect(() => {
    refetch();
  }, [selectedLabels]);

  const dataExperiences = [
    ...(data?.experiences?.edges?.map((item: Dic<any>) => ({
      ...item.node,
    })) ?? []),
  ];

  const experiences = devMode
    ? dataExperiences
    : dataExperiences.filter(
        (experience: Dic<any>) => !experience?.settings?.interface?.hidden,
      );

  const handleEdit = (id: string) => {
    potionsLocalStorage.set("ExperienceTab", "Configure");
    if (flagValueEarlyAdopterBuilder && searchType == "MULTIPLY") {
      window.open("https://app2.abtasty.com/recommendations", "_blank");
      return;
    }
    if (hasBuilder) {
      history.push(
        `/${organisationId}/${siteId}/${
          mapSearchTypeToKeyBuilder[searchType]
        }_builder/${id === "create" ? "" : `${id}`}`,
      );
    } else {
      history.push(
        `/${organisationId}/${siteId}/${mapSearchTypeToKey[searchType]}/${id}`,
      );
    }
  };

  const handleReport = (id: string) => {
    potionsLocalStorage.set("ExperienceTab", "Report");
    history.push(
      `${location.pathname}/${id}?tab=experience&experienceId=${id}&noSelector=true`,
    );
  };

  const handleClose = () => {
    setExperienceToDelete({ id: "", name: "" });
  };

  const handleDelete = React.useCallback((id: string, name: string) => {
    setExperienceToDelete({ id, name });
  }, []);

  const handleDuplicate = (experienceToDuplicate: Dic<any>): void => {
    setStatus("loading");
    client
      .mutate({
        mutation: DUPLICATE_EXPERIENCE,
        variables: {
          experienceId: experienceToDuplicate?.id,
          destinationSiteId: siteId,
        },
      })
      .then(() => {
        refetch();
        dispatch(
          sendNotification(
            "duplicate_experience",
            t("duplicate"),
            t("duplicate_experience_success"),
            "success",
          ),
        );
      })
      .catch((err: any) => {
        dispatch(
          sendNotification(
            "duplicate_experience",
            t("duplicate"),
            `${t("error")}: ${err.message}`,
            "error",
          ),
        );
      });
  };

  const noExperiences = experiences.length === 0;

  const newRows = React.useMemo(
    () =>
      experiences.map((experience: Dic<any>) => ({
        ...experience,
        createdBy: experience?.collection?.createdByUser?.email ?? "",
        createdAt: experience?.collection?.createdAt ?? "",
        updatedAt: experience?.collection?.updatedAt ?? "",
        canCreate,
        canDelete,
        canEdit: canEdit && experience.isConfigurable,
        canReport:
          experiencesUtils.hasReport(
            experience.type,
            experience.isMonitorable,
          ) && experience?.deployedAt,
        report: () => handleReport(experience.id),
        delete: () => handleDelete(experience.id, experience.name),
        edit: () => handleEdit(experience.id),
        duplicate: () => handleDuplicate(experience),
        goToSettings: () =>
          history.replace(
            `/${organisationId}/${siteId}/recommendations/settings/${experience.id}`,
          ),
        isPreview: experience.previews.edges.length > 0,
        location: [t(experience.pageType?.toLowerCase()), experience.uuid],
        previewLink: experience.previews.edges?.[0]?.node?.link ?? null,
        isActive: !!experience.deployedAt,
        activationDate: experience.deployedAt
          ? dayjs(experience.deployedAt).format("L")
          : "",
        userRate: () => getOverviewData("users_over_exposed", experience),
        weightInCa: () => getOverviewData("revenues_from_users", experience),
        impact: () =>
          getOverviewData(
            "revenues_per_users_over_revenues_per_exposed",
            experience,
          ),
      })),
    [
      experiences,
      canCreate,
      canDelete,
      canEdit,
      handleDelete,
      handleDuplicate,
      handleEdit,
      handleReport,
      history,
      organisationId,
      siteId,
      t,
    ],
  );

  const sortedRows = React.useMemo(() => {
    if (hasBuilder) {
      return newRows;
    }
    return newRows.sort(compareExperiences);
  }, [newRows]);

  const [show, setShow] = React.useState(true);

  return React.useMemo(
    () => ({
      canEdit,
      handleEdit,
      handleClose,
      experienceToDelete,
      refetch,
      noExperiences,
      show,
      totalCount,
      newRows: sortedRows,
      filteredColumns,
      gettingExperiences: loading,
      handleReport,
      canCreate,
      ...pageInfos,
      next,
      previous,
      pageSize,
      handleChangePageSize,
      currentPage,
      loading,
    }),
    [
      canCreate,
      canEdit,
      handleEdit,
      handleClose,
      experienceToDelete,
      refetch,
      noExperiences,
      show,
      totalCount,
      sortedRows,
      filteredColumns,
      status,
      handleReport,
      pageInfos,
      pageSize,
      handleChangePageSize,
      currentPage,
      totalCount,
      loading,
    ],
  );
};

export default useExperienceList;
